<template>
	<div class="layout-wrapper layout-content-navbar" :class="{ 'has-unpaid-banner': isSuspendedForUnpaid }">
		<!-- Bannière d'impayés -->
		<div class="unpaid-banner" v-if="profile && isSuspendedForUnpaid">
			<div class="container-fluid px-4">
				<div class="d-flex align-items-center">
					<i class="ri-error-warning-line ri-24px me-3"></i>
					<div>
						<strong>{{ $t('layout.unpaidBanner.title') }}</strong>
						<p class="mb-0">{{ $t('layout.unpaidBanner.message') }}</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Compte suspendu pour impayés : modal bloquant, non fermable -->
		<div class="suspended-overlay" v-if="profile && isSuspendedForUnpaid" role="alertdialog" aria-modal="true" aria-labelledby="suspended-title">
			<div class="suspended-card">
				<i class="ri-error-warning-line suspended-icon"></i>
				<h4 id="suspended-title" class="mb-3">{{ $t('layout.suspendedModal.title') }}</h4>
				<p class="mb-4">
					{{ $t('layout.suspendedModal.messageBefore') }}
					<a href="mailto:contact@pika-aero.com" class="fw-medium">contact@pika-aero.com</a>
					{{ $t('layout.suspendedModal.messageAfter') }}
				</p>
				<button type="button" class="btn btn-outline-secondary" @click="logout()">
					<i class="ri-shut-down-line me-2"></i>{{ $t('layout.suspendedModal.logout') }}
				</button>
			</div>
		</div>

		<div class="layout-container" v-if="profile">
			<!-- Menu Component -->
			<Menu :displayOpco="displayOpco" :displayRequests="displayRequests" :role="profile.role" :backgroundCheckEnabled="backgroundCheckEnabled" :displayMicroLearning="displayMicroLearning" :displayVirtualClassrooms="displayVirtualClassrooms" :displayAgencies="displayAgencies" />

			<!-- Layout container -->
			<div
				:class="isMenuExpanded ? 'layout-page' : 'layout-page'"
			>
				<nav
					class="layout-navbar container-xxl navbar navbar-expand-xl navbar-detached align-items-center bg-navbar-theme"
					id="layout-navbar">
					<div class="layout-menu-toggle navbar-nav align-items-xl-center me-4 me-xl-0 d-xl-none">
						<a class="nav-item nav-link px-0 me-xl-6" @click="toggleMenu()">
							<i class="ri-menu-fill ri-22px"></i>
						</a>
					</div>

					<div class="navbar-nav-right d-flex align-items-center" id="navbar-collapse">
						<ul class="navbar-nav flex-row align-items-center ms-auto" style="padding-right: 24px;">
							<LanguageSwitcher />
							<!-- User -->
							<li class="nav-item navbar-dropdown dropdown-user dropdown">
								<a class="nav-link dropdown-toggle hide-arrow" href="javascript:void(0);" data-bs-toggle="dropdown">
									<div class="avatar avatar">
										<span class="avatar-initial rounded-circle bg-label-primary">
											{{ getInitials() }}
										</span>
									</div>
								</a>
								<ul class="dropdown-menu dropdown-menu-end">
									<li>
										<a class="dropdown-item" href="#">
											<div class="d-flex">
												<div class="flex-shrink-0 me-2">
													<div class="avatar avatar">
														<span class="avatar-initial rounded-circle bg-label-primary">
															{{ getInitials() }}
														</span>
													</div>
												</div>
												<div class="flex-grow-1" v-if="profile">
													<span class="fw-medium d-block">
														{{ profile.firstName }} {{ profile.lastName }}
													</span>
													<small class="text-muted">{{ $t('common.roles.manager') }}</small>
												</div>
											</div>
										</a>
									</li>
									<li>
										<div class="dropdown-divider"></div>
									</li>
									<li>
										<router-link to="/settings" class="dropdown-item">
											<i class="ri-settings-4-line me-3"></i><span class="align-middle">{{ $t('layout.settings') }}</span>
										</router-link>
									</li>
									<li>
										<a class="dropdown-item" href="javascript:void(0);" @click="logout()">
											<i class="ri-shut-down-line me-3"></i>
											<span class="align-middle">{{ $t('layout.logout') }}</span>
										</a>
									</li>
								</ul>
							</li>
							<!--/ User -->
						</ul>
					</div>
				</nav>

				<div class="layout-overlay layout-menu-toggle"></div>

				<!-- Content wrapper -->
				<div class="content-wrapper">
					<div class="container-xxl flex-grow-1 container-p-y">
						<!-- Vue Router View -->
						<router-view />
					</div>
				</div>

				<!-- Footer Component -->
				<Footer />
			</div>
		</div>
	</div>
</template>

<script>
import { Dropdown } from 'bootstrap'

import Menu from "@/components/Menu.vue";
import Footer from "@/components/Footer.vue";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";

import HTTP from "@/axios";
import * as Sentry from "@sentry/vue";

export default {
	name: "DefaultLayout",
	components: {
		Menu,
		Footer,
		LanguageSwitcher,
	},

	data() {
		return {
			profile: null,
			isMenuExpanded: false,
			isSuspendedForUnpaid: false,
			displayOpco: false,
			displayRequests: false,
			backgroundCheckEnabled: false,
			displayMicroLearning: false,
			displayVirtualClassrooms: false,
			displayAgencies: false,
		}
	},

	async mounted() {
		// Ajouter dynamiquement les attributs nécessaires à <html> pour le thème
		document.documentElement.setAttribute("lang", this.$i18n.locale);
		document.documentElement.setAttribute(
			"class",
			"light-style layout-navbar-fixed layout-menu-fixed layout-compact"
		);
		document.documentElement.setAttribute("dir", "ltr");
		document.documentElement.setAttribute("data-theme", "theme-default");
		document.documentElement.setAttribute(
			"data-template",
			"vertical-menu-template-no-customizer-starter"
		);
		document.documentElement.setAttribute("data-style", "light");

		if (this.$store.getters.isLoggedIn)
			this.fetchProfile();

		const dropdownTriggerList = this.$el.querySelectorAll('[data-bs-toggle="dropdown"]')
		dropdownTriggerList.forEach(el => {
			new Dropdown(el)
		})
	},

	watch: {
		isSuspendedForUnpaid(value) {
			document.body.style.overflow = value ? "hidden" : "";
		}
	},

	beforeUnmount() {
		document.body.style.overflow = "";
	},

	methods: {
		getInitials() {
			if (!this.profile) return '';
			const firstInitial = this.profile.firstName ? this.profile.firstName.trim().charAt(0).toUpperCase() : '';
			const lastInitial = this.profile.lastName ? this.profile.lastName.trim().charAt(0).toUpperCase() : '';
			return firstInitial + lastInitial;
		},

		toggleMenu() {
			this.$store.dispatch("toggleMenu");
		},

		async fetchProfile() {
			try {
				const response = await HTTP(`/user/profile`);
				this.profile = response.data.profile;

				Sentry.setUser({
					email: this.profile.email,
					username: `${this.profile.firstName} ${this.profile.lastName}`,
				});

				Sentry.captureMessage(`[Login] ${this.profile.firstName} ${this.profile.lastName} (${this.profile.email})`, 'info');

				this.displayOpco = response.data.displayOpco;
				this.displayRequests = response.data.displayRequests;
				this.backgroundCheckEnabled = response.data.backgroundCheckEnabled || false;
				this.displayMicroLearning = response.data.displayMicroLearning || false;
				this.displayVirtualClassrooms = response.data.displayVirtualClassrooms || false;

				// Compte suspendu pour impayés (companies.metadata.unpaid) : bannière + modal bloquant
				this.isSuspendedForUnpaid = response.data.isSuspendedForUnpaid === true;

				// displayAgencies vient directement de la company (metadata).
				try {
					const companyResponse = await HTTP(`/company`);
					this.displayAgencies = !!companyResponse.data.company?.metadata?.displayAgencies;
				} catch (companyErr) {
					console.error("[Layout] Unable to fetch company flags:", companyErr);
				}
			} catch (error) {
				// Récupérer le message d'erreur de l'API ou définir un message par défaut
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');

				alert(errorMessage);

				// Log détaillé dans la console (utile en développement)
				console.error("Erreur API:", error);
			}
		},

		logout() {
			if (!confirm(this.$t('confirm.logout')))
				return;

			Sentry.setUser(null);
			this.$store.dispatch("logout");

			// Setup company
			this.$router.push("/login");
		}
	}
};
</script>

<style scoped>
.unpaid-banner {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	width: 100%;
	z-index: 10000 !important;
	background-color: #dc3545;
	color: white;
	padding: 16px 0;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.unpaid-banner i {
	flex-shrink: 0;
}

.unpaid-banner strong {
	display: block;
	font-size: 16px;
	margin-bottom: 4px;
}

.unpaid-banner p {
	font-size: 14px;
	line-height: 1.4;
}

.suspended-overlay {
	position: fixed;
	inset: 0;
	z-index: 10001;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background-color: rgba(20, 20, 30, 0.75);
	backdrop-filter: blur(4px);
}

.suspended-card {
	max-width: 480px;
	width: 100%;
	padding: 32px 28px;
	background: #fff;
	border-radius: 12px;
	border-top: 6px solid #dc3545;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
	text-align: center;
}

.suspended-icon {
	display: block;
	font-size: 56px;
	color: #dc3545;
	margin-bottom: 12px;
}

/* Ajouter un padding-top au layout-wrapper pour compenser la bannière fixe */
.layout-wrapper.has-unpaid-banner {
	padding-top: 70px;
}
</style>
